import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    InputNumber,
    DatePicker,
    message,
    Popconfirm,
    Space,
    Card,
    Typography,
    Row,
    Col,
    Tag,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    CalendarOutlined,
    UserOutlined,
    TagOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function Articles() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [form] = Form.useForm();

    const categories = [
        { value: 'news', label: 'News' },
        { value: 'blog', label: 'Blog' },
        { value: 'announcement', label: 'Announcement' },
        { value: 'feature', label: 'Feature Story' },
        { value: 'update', label: 'Update' },
    ];

    useEffect(() => {
        fetchArticles();
    }, []);

    const fetchArticles = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetArticles();
            if (response.success) {
                setArticles(response.data.data || []);
            }
        } catch (error) {
            message.error('Failed to fetch articles');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            const data = {
                ...values,
                published_at: values.published_at ? values.published_at.format('YYYY-MM-DD HH:mm:ss') : null,
                tags: values.tags ? values.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
            };

            if (editingArticle) {
                await api.adminUpdateArticle(editingArticle.id, data);
                message.success('Article updated successfully');
            } else {
                await api.adminCreateArticle(data);
                message.success('Article created successfully');
            }
            setModalVisible(false);
            setEditingArticle(null);
            form.resetFields();
            fetchArticles();
        } catch (error) {
            message.error('Failed to save article');
        }
    };

    const handleEdit = (article) => {
        setEditingArticle(article);
        form.setFieldsValue({
            ...article,
            published_at: article.published_at ? dayjs(article.published_at) : null,
            tags: article.tags ? article.tags.join(', ') : '',
            is_published: article.is_published,
            is_featured: article.is_featured,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteArticle(id);
            message.success('Article deleted successfully');
            fetchArticles();
        } catch (error) {
            message.error('Failed to delete article');
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-sm text-gray-500">/{record.slug}</div>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => {
                const categoryInfo = categories.find(cat => cat.value === category);
                return (
                    <Tag color="blue">
                        {categoryInfo?.label || category}
                    </Tag>
                );
            },
        },
        {
            title: 'Author',
            dataIndex: 'author',
            key: 'author',
            render: (author) => (
                <div className="flex items-center">
                    <UserOutlined className="mr-1" />
                    {author || 'Unknown'}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {record.is_published ? (
                        <Tag color={getStatusColor('success')}>Published</Tag>
                    ) : (
                        <Tag color={getStatusColor('pending')}>Draft</Tag>
                    )}
                    {record.is_featured && <Tag color="gold">Featured</Tag>}
                </Space>
            ),
        },
        {
            title: 'Published',
            dataIndex: 'published_at',
            key: 'published_at',
            render: (date) => (
                <div className="flex items-center">
                    <CalendarOutlined className="mr-1" />
                    {date ? dayjs(date).format('MMM DD, YYYY') : 'Not published'}
                </div>
            ),
        },
        {
            title: 'Views',
            dataIndex: 'views',
            key: 'views',
            render: (views) => (
                <div className="flex items-center">
                    <EyeOutlined className="mr-1" />
                    {views || 0}
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Article">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            href={`/articles/${record.slug}`}
                            target="_blank"
                        >
                            View
                        </Button>
                    </Tooltip>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this article?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>Articles Management</Title>
                <p className="text-gray-600">
                    Manage news articles, blog posts, and announcements for your website
                </p>
            </div>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <div>
                        <Title level={4} className="!mb-0">
                            All Articles ({articles.length})
                        </Title>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingArticle(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                    >
                        Add Article
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={articles}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} articles`,
                    }}
                />
            </Card>

            <Modal
                title={editingArticle ? 'Edit Article' : 'Add New Article'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingArticle(null);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        category: 'news',
                        is_published: false,
                        is_featured: false,
                        sort_order: 0,
                    }}
                >
                    <Form.Item
                        name="title"
                        label="Article Title"
                        rules={[{ required: true, message: 'Please enter article title' }]}
                    >
                        <Input placeholder="Enter article title" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="slug"
                                label="URL Slug"
                            >
                                <Input placeholder="auto-generated-from-title" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select category' }]}
                            >
                                <Select placeholder="Select category">
                                    {categories.map(category => (
                                        <Option key={category.value} value={category.value}>
                                            {category.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="excerpt"
                        label="Excerpt"
                    >
                        <TextArea
                            rows={3}
                            placeholder="Enter article excerpt..."
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Content"
                        rules={[{ required: true, message: 'Please enter article content' }]}
                    >
                        <TextArea
                            rows={8}
                            placeholder="Enter article content (HTML supported)..."
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="author"
                                label="Author"
                            >
                                <Input placeholder="Enter author name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="featured_image"
                                label="Featured Image URL"
                            >
                                <Input placeholder="Enter featured image URL" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="tags"
                        label="Tags (comma-separated)"
                    >
                        <Input placeholder="tag1, tag2, tag3" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="is_published"
                                label="Published"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="is_featured"
                                label="Featured"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="sort_order"
                                label="Sort Order"
                            >
                                <InputNumber min={0} placeholder="0" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="published_at"
                        label="Publish Date"
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            placeholder="Select publish date"
                            className="w-full"
                        />
                    </Form.Item>

                    <div className="flex justify-end space-x-2">
                        <Button onClick={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingArticle ? 'Update' : 'Create'} Article
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default Articles;
